import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { ViewSwitch } from '../components/ViewSwitch';
import { cn } from '../lib/utils';
import { getProductDisplayName } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  Trophy, 
  DollarSign, 
  Image, 
  Loader2,
  Target,
  ChevronRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function WinningProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [winningDetails, setWinningDetails] = useState([]);
  const [winningImages, setWinningImages] = useState([]);
  const [marketingAngles, setMarketingAngles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, detailsRes, imagesRes, marketingRes] = await Promise.all([
        supabase.from('products').select('ecom_id, url, platform, image_urls'),
        supabase.from('winning_product_details').select('*'),
        supabase.from('winning_product_images').select('*'),
        supabase.from('product_marketing_angles').select('*'),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (detailsRes.error) throw detailsRes.error;
      if (imagesRes.error) throw imagesRes.error;

      setProducts(productsRes.data || []);
      setWinningDetails(detailsRes.data || []);
      setWinningImages(imagesRes.data || []);
      setMarketingAngles(marketingRes.data || []);
    } catch (error) {
      console.error('Error fetching winning products:', error);
      toast.error('Failed to load winning products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getProductInfo = (ecomId) => products.find(p => p.ecom_id === ecomId);
  const getImages = (ecomId) => winningImages.find(wi => wi.ecom_id === ecomId);
  const getMarketing = (ecomId) => marketingAngles.find(m => m.ecom_id === ecomId);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="winning-products-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1E1B4B] font-['Manrope']">
              {t('winning.title')}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {t('winning.subtitle', { count: winningDetails.length })}
            </p>
          </div>
          <ViewSwitch view={view} onViewChange={setView} />
        </div>

        {winningDetails.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Trophy className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {t('winning.emptyTitle')}
            </h3>
            <p className="text-slate-500">
              {t('winning.emptySubtitle')}
            </p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {winningDetails.map((detail) => {
              const productInfo = getProductInfo(detail.ecom_id);
              const images = getImages(detail.ecom_id);
              const marketing = getMarketing(detail.ecom_id);
              const heroImage = images?.image1;
              const imageCount = [images?.image1, images?.image2, images?.image3, images?.image4, images?.image5].filter(Boolean).length;

              return (
                <div
                  key={detail.id}
                  onClick={() => navigate(`/winning-products/${detail.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer',
                    'hover:shadow-lg hover:border-amber-300 transition-all group'
                  )}
                  data-testid={`winning-product-${detail.ecom_id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-slate-100 overflow-hidden relative">
                    {heroImage ? (
                      <img 
                        src={heroImage} 
                        alt={detail.product_name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Trophy className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    {detail.price && (
                      <div className="absolute bottom-2 left-2 bg-emerald-500 text-white px-2 py-1 rounded text-xs font-bold">
                        ${detail.price}
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-[#1E1B4B] text-sm line-clamp-2 mb-2">
                      {detail.product_name || detail.ecom_id}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={detail.status} lane="winning" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {winningDetails.map((detail) => {
              const productInfo = getProductInfo(detail.ecom_id);
              const images = getImages(detail.ecom_id);
              const marketing = getMarketing(detail.ecom_id);
              const heroImage = images?.image1;
              const imageCount = [images?.image1, images?.image2, images?.image3, images?.image4, images?.image5].filter(Boolean).length;

              return (
                <div
                  key={detail.id}
                  onClick={() => navigate(`/winning-products/${detail.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer',
                    'hover:shadow-lg hover:border-amber-300 transition-all group'
                  )}
                  data-testid={`winning-row-${detail.ecom_id}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Image */}
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                      {heroImage ? (
                        <img src={heroImage} alt={detail.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#1E1B4B] truncate text-sm md:text-base">
                        {detail.product_name || detail.ecom_id}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <StatusBadge status={detail.status} lane="winning" />
                        {detail.price && (
                          <span className="text-xs font-bold text-emerald-600">${detail.price}</span>
                        )}
                        <span className="text-xs text-slate-500">{imageCount} images</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
